import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {makeStyles} from '@material-ui/core'
import HealthCenterReportStyle from 'pages/HealthCenter/ReportV1/Style'
import WrapElement from 'pages/HealthCenter/ReportV1/Elements/WrapElement'
import Analysis from 'pages/HealthCenter/ReportV1/Elements/Analysis'
import {getPublicFile} from 'helpers/envHelper'
import PercentBarCardV4 from 'pages/HealthCenter/ReportV1/BrainFunctionalConnectivity/Elements/PercentBarCardV4'
import {useAppSelector} from 'hooks'
import {
  getCurrentLanguageReport,
  selectLanguageReport,
} from 'features/languageReport/languageReportSlice'

const useStyles = makeStyles(HealthCenterReportStyle)

export interface BrainFunctionalConnectivityProps {
  partTwoImg: string
  partThreeImg: string
  brainConnectivityBetweenLobesScore: {
    vertical: {
      left: number
      right: number
    }
    horizontal: number
  } | null
  brainConnectivityByLobeScore: {
    frontal: {
      left: number
      right: number
    }
    pariocci: {
      left: number
      right: number
    }
  } | null
}

const PagePrintWrap3 = ({
  partTwoImg,
  partThreeImg,
  brainConnectivityByLobeScore,
  brainConnectivityBetweenLobesScore,
}: BrainFunctionalConnectivityProps) => {
  const {t} = useTranslation()
  const classes = useStyles()
  const getHealthReportReportImage = getPublicFile(
    'images/health_center_report',
  )
  const languageReport = useAppSelector((state) =>
    selectLanguageReport(state.languageReport),
  )
  const currentLanguagueReport = getCurrentLanguageReport(languageReport, 'es')

  const textBrainConnectivity: any = {
    ...currentLanguagueReport?.health?.brain_connectivity,
  }

  const intraData = {...textBrainConnectivity.section.intra_connectivity}
  const getNumber = (value: number | string) => {
    if (!Number.isNaN(Number(value))) return Math.floor(Number(value))
    return 0
  }

  const getText = (value = 0, key: string) => {
    if (value < 30) {
      return t(`IBot${key}`, {
        value: getNumber(value),
      })
    }
    if (value > 70) {
      return t(`ITop${key}`, {
        value: getNumber(value),
      })
    }
    return t(`IMid${key}`, {
      value: getNumber(value),
    })
  }

  const textPart3Title1 = useMemo(() => {
    const score = brainConnectivityByLobeScore?.frontal?.left ?? 0
    const data = intraData.analysis_results.opinions.frontal
    if (score < 30) {
      return data.left.low.main.replace('{0}', getNumber(score).toString())
    }
    if (score > 70) {
      return data.left.high.main.replace('{0}', getNumber(score).toString())
    }
    return data.left.normal.main.replace('{0}', getNumber(score).toString())
  }, [brainConnectivityByLobeScore?.frontal?.left])

  const textPart3Text1 = useMemo(() => {
    const score = brainConnectivityByLobeScore?.frontal?.left ?? 0
    const data = intraData.analysis_results.opinions.frontal
    if (score < 30) {
      return data.left.low.sub
    }
    if (score > 70) {
      return data.left.high.sub
    }
    return data.left.normal.sub
  }, [brainConnectivityByLobeScore?.frontal?.left])

  const textPart3Title2 = useMemo(() => {
    const score = brainConnectivityByLobeScore?.frontal?.right ?? 0
    const data = intraData.analysis_results.opinions.frontal
    if (score < 30) {
      return data.right.low.main.replace('{0}', getNumber(score).toString())
    }
    if (score > 70) {
      return data.right.high.main.replace('{0}', getNumber(score).toString())
    }
    return data.right.normal.main.replace('{0}', getNumber(score).toString())
  }, [brainConnectivityByLobeScore?.frontal?.right])
  const textPart3Text2 = useMemo(() => {
    const score = brainConnectivityByLobeScore?.frontal?.right ?? 0
    const data = intraData.analysis_results.opinions.frontal
    if (score < 30) {
      return data.right.low.sub
    }
    if (score > 70) {
      return data.right.high.sub
    }
    return data.right.normal.sub
  }, [brainConnectivityByLobeScore?.frontal?.right])

  const textPart3Title3 = useMemo(() => {
    const score = brainConnectivityByLobeScore?.pariocci?.left ?? 0
    const data = intraData.analysis_results.opinions.pariocci
    if (score < 30) {
      return data.left.low.main.replace('{0}', getNumber(score).toString())
    }
    if (score > 70) {
      return data.left.high.main.replace('{0}', getNumber(score).toString())
    }
    return data.left.normal.main.replace('{0}', getNumber(score).toString())
  }, [brainConnectivityByLobeScore?.pariocci?.left])
  const textPart3Text3 = useMemo(() => {
    const score = brainConnectivityByLobeScore?.pariocci?.left ?? 0
    const data = intraData.analysis_results.opinions.pariocci
    if (score < 30) {
      return data.left.low.sub
    }
    if (score > 70) {
      return data.left.high.sub
    }
    return data.left.normal.sub
  }, [brainConnectivityByLobeScore?.pariocci?.left])

  const textPart3Title4 = useMemo(() => {
    const score = brainConnectivityByLobeScore?.pariocci?.right ?? 0
    const data = intraData.analysis_results.opinions.pariocci
    if (score < 30) {
      return data.right.low.main.replace('{0}', getNumber(score).toString())
    }
    if (score > 70) {
      return data.right.high.main.replace('{0}', getNumber(score).toString())
    }
    return data.right.normal.main.replace('{0}', getNumber(score).toString())
  }, [brainConnectivityByLobeScore?.pariocci?.right])

  const textPart3Text4 = useMemo(() => {
    const score = brainConnectivityByLobeScore?.pariocci?.right ?? 0
    const data = intraData.analysis_results.opinions.pariocci
    if (score < 30) {
      return data.right.low.sub
    }
    if (score > 70) {
      return data.right.high.sub
    }
    return data.right.normal.sub
  }, [brainConnectivityByLobeScore?.pariocci?.right])

  return (
    <div className={classes.brainBasic}>
      <WrapElement number={3} text={intraData.title}>
        <div>
          <div className={classes.brainConnectivityBrainRegionTabs3}>
            <div
              className={`${classes.cardLeftContent} ${classes.cardLeftContentAdditionalV1}`}
            >
              <img
                className={classes.cardLeftImg}
                src={getHealthReportReportImage('fourDivisionsBase.webp')}
                alt='Contour'
                style={{width: '285px'}}
              />
            </div>
            <div className={classes.percentBarCar}>
              <PercentBarCardV4
                index={1}
                headerName={intraData.gauge_bars.frontal.left.title}
                percentage={getNumber(
                  brainConnectivityByLobeScore?.frontal?.left ?? 0,
                )}
                top={intraData.gauge_bars.frontal.left.status.low}
                middle={intraData.gauge_bars.frontal.left.status.normal}
                bottom={intraData.gauge_bars.frontal.left.status.high}
              />
              <PercentBarCardV4
                index={3}
                headerName={intraData.gauge_bars.frontal.right.title}
                percentage={getNumber(
                  brainConnectivityByLobeScore?.frontal?.right ?? 0,
                )}
                top={intraData.gauge_bars.frontal.right.status.low}
                middle={intraData.gauge_bars.frontal.right.status.normal}
                bottom={intraData.gauge_bars.frontal.right.status.high}
              />
              <PercentBarCardV4
                index={2}
                headerName={intraData.gauge_bars.pariocci.left.title}
                percentage={getNumber(
                  brainConnectivityByLobeScore?.pariocci?.left ?? 0,
                )}
                top={intraData.gauge_bars.pariocci.left.status.low}
                middle={intraData.gauge_bars.pariocci.left.status.normal}
                bottom={intraData.gauge_bars.pariocci.left.status.high}
              />

              <PercentBarCardV4
                index={4}
                top={intraData.gauge_bars.pariocci.right.status.low}
                headerName={intraData.gauge_bars.pariocci.right.title}
                percentage={getNumber(
                  brainConnectivityByLobeScore?.pariocci?.right ?? 0,
                )}
                middle={intraData.gauge_bars.pariocci.right.status.normal}
                bottom={intraData.gauge_bars.pariocci.right.status.high}
              />
            </div>
          </div>

          <div
            className={`${classes.cardRightContent} ${classes.cardRightContentAdditional}`}
          >
            <div>
              <p className={classes.textBold}>{intraData.description.main}</p>
              <br />
              <p className={classes.textBlack}>{intraData.description.sub}</p>
            </div>

            <Analysis title={intraData.analysis_results.title}>
              <div className={classes.connectivityBrain}>
                <p className={classes.textBold}>{textPart3Title1}</p>
                <p className={classes.textP} style={{marginBottom: 16}}>
                  {textPart3Text1}
                </p>

                <p className={classes.textBold}>{textPart3Title2}</p>
                <p className={classes.textP} style={{marginBottom: 16}}>
                  {textPart3Text2}
                </p>

                <p className={classes.textBold}>{textPart3Title3}</p>
                <p className={classes.textP} style={{marginBottom: 16}}>
                  {textPart3Text3}
                </p>

                <p className={classes.textBold}>{textPart3Title4}</p>
                <p className={classes.textP}>{textPart3Text4}</p>
              </div>
            </Analysis>
          </div>
        </div>
      </WrapElement>
    </div>
  )
}

export default PagePrintWrap3
